import { createFileRoute } from "@tanstack/react-router";

import { listContent } from "@/lib/cms.functions";
import { normalizeLocale } from "@/lib/i18n";
import { pageHead } from "@/content/seo-pages";
import { ContentGrid, PageHero } from "@/components/site/ContentViews";
import { IsoIllustration } from "@/components/site/Iso";
import { Section } from "@/components/site/Section";
import { ClosingCta } from "@/components/site/Cta";
import { useT } from "@/lib/i18n-dict";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { FaqSection } from "@/components/site/FaqSection";
import { INDUSTRIES_FAQ } from "@/content/industry-faq";
import { faqScript } from "@/lib/faq-schema";

export const Route = createFileRoute("/{-$locale}/industries/")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const items = await listContent({ data: { kind: "industry", locale } });
    return { locale, items };
  },
  head: ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const head = pageHead("/industries", locale, {
      title: "Industries | factory.online",
      description:
        "How factory.online supports discrete, process and mixed-mode manufacturers across industries.",
    });
    return { ...head, scripts: faqScript(INDUSTRIES_FAQ, locale) };
  },
  component: IndustriesIndex,
});

function IndustriesIndex() {
  const { items, locale } = Route.useLoaderData();
  const t = useT();
  return (
    <>
      <PageHero
          eyebrow={t("Industries")}
          title={t("Manufacturing software for your industry")}
          lead={t("From metalworking and cable production to food and chemicals — planning, inventory and costing configured for your production model.")}
        />
      <Section className="pt-0">
        <IsoIllustration
          name="smartFactory"
          alt="Isometric smart factory production scene"
          className="mx-auto mb-14 w-full max-w-2xl"
        />
        <ContentGrid
          items={items}
          base="/industries"
          locale={locale}
          emptyLabel="Nothing published here yet."
          showDate={false}
        />
      </Section>
      <FaqSection faqs={INDUSTRIES_FAQ} title="Industry questions, answered" />
      <RelatedLinks variant="industry" />
      <ClosingCta />
    </>
  );
}
